<template>
  <section class="w-full h-full">
    <!--=====================================
      =              TabSwitcher             =
      ======================================-->
    <div class="w-full h-full bg-white text-center pt-2 pb-3"/>
    <TabSwitcher
      @tab-change="handleTabChange"
      v-model="activeTab"
      :tabs="tabs"
      v-if="!$route.params.plan && canViewInsights"
    />
    
    <!--=====================================
    =           Essentials and Insights     =
    ======================================-->
    <transition name="fade" appear mode="out-in">
      <keep-alive>
        <router-view name="options" />
      </keep-alive>
    </transition>

    <template v-if="!$route.params.plan && pricingEssentialsMounted">
      <div v-if="!activeTab">
        <PlansFeaturesCards />
        <div class="bg-white w-full text-center py-16 flex justify-center flex-col space-y-6">
          <div class="flex w-full">
            <div class="cta-heading leading-10 font-bold text-4xl m-auto"><span class="text-green-300">Upgrade</span>&nbsp;and Expand Your Recruiting Efforts</div>
          </div>
            <div class="flex justify-center">
            <ChargbeeModal :plan-id="selectedPlan" :checkout-data="checkoutJsData" :redirect-to="redirectTo">
              <Button
                text="Upgrade to Pro"
                class="sm:flex upgrade-to-pro-btn w-auto sm:w-auto"
                color="green"
                icon="sr-2 sr-v2-arrow-right-circle origin-center translate-y-1"
                :icon-options="{customClasses: 'transform -rotate-90'}"
                textColor="ds-text-xl text-white"
                emphasis="high"
                @click.native.prevent="showMonthlyAndAnnualOptions ? purchaseProduct() : props.startCheckout()"
                slot="button"
                slot-scope="props"
              />
            </ChargbeeModal>
          </div>
        </div>
        <GuidanceCTA 
          @handleTabChange="handleTabChange"
          :tabs="tabs"
          :activeTab="activeTab"
          :isExpiringIntegratedUser="isExpiringIntegratedUser"
        />
      </div>
    <!-- =============================================
    =                      FAQ                      =
    ============================================= -->
      <div class="w-full bg-gray-1000 pb-16">
        <div class="max-w-1200 mx-auto">
          <div class="text-white px-4 py-8">
            <p
              class="text-white text-3xl ds-text-3xl font-bold text-center mb-6"
            >
              Frequently Asked Questions
            </p>

            <accordion
              v-model="activeFaq"
              :sections="faqs"
              class="divide-solid divide-y divide-white mb-6 mx-auto"
              header-class="first:border-t last:border-b border-l-0 border-r-0 border-white"
              style="max-width: 768px"
              @sectionOpened="trackFaq"
            ></accordion>
          </div>
        </div>
      </div>
    </template>

    <ScheduleCall class="schedule-call-widget fixed right-2 bottom-2 z-20" />
  </section>
</template>

<script>
import tracking from "./utils/tracking";
import TabSwitcher from "./components/TabSwitcher";
import ScheduleCall from "./components/ScheduleCall";
import Accordion from "./components/Accordion";
import Carousel from "./components/Carousel";
import GuidanceCTA from "./components/GuidanceCTA";
import UserAvatar from "@/components/UserAvatar";
import PlansFeaturesCards from './Essentials/PlanFeatures/PlansFeaturesCards.vue'
import { Button } from '@sportsrecruits/playbook';
import { mapState } from "vuex";
import ChargbeeModal from "@/srui/components/ChargbeeModal";

export default {
  name: "Pricing",
  components: {
    Carousel,
    Accordion,
    TabSwitcher,
    ScheduleCall,
    UserAvatar,
    Button,
    GuidanceCTA,
    PlansFeaturesCards,
    ChargbeeModal
  },
  data() {
    return {
      redirectTo: "/activity/everyone/all/welcome/essentials",
      checkoutJsData: window.pricing.checkoutJsData,
      tabs: [
        {
          id: 0,
          shortLabel: "Pro Account",
          label: "Pro Account",
          route: "Pricing/Essentials",
        },
        {
          id: 1,
          shortLabel: "Guidance",
          label: "Recruiting Guidance",
          route: "Pricing/Insights",
        },
      ],
      activeComponent: null,
      activeFaq: null,
      activeTestimony: null,
      canViewInsights: true,
      isExpiringIntegratedUser: null,
      hasMounted: false,
    };
  },
  computed: {
    ...mapState("pricing", {
      activeTab: (state) => state.activeTab,
      selectedPlan: (state) => state.selectedPlan,
      products: (state) => state.products,
      featureGroups: (state) => state.featureGroups,
      testimonials: (state) => state.testimonials,
      faqs: (state) => state.faqs,
      pricingEssentialsMounted: (state) => state.pricingEssentialsMounted
    }),
    showMonthlyAndAnnualOptions() {
      return (
        this.$domain.config("shop_page.pro_plan.monthly.enabled") &&
        this.$domain.config("shop_page.pro_plan.annual.enabled")
      );
    },
  },
  watch: {
    activeTestimony(index) {
      let testimony = this.$store.getters["pricing/getTestimony"](index);

      if (testimony) {
        this.trackTestimony(testimony);
      }
    },
  },
  methods: {
    handleTabChange(tab) {
      if (this.$route.name !== tab.route) {
        this.$router.push({ name: tab.route });
      }
    },
    scheduleCall(calendlyLink, segmentData) {
      tracking.calendly(calendlyLink, segmentData);
    },
    trackFaq(index) {
      let faq = this.$store.getters["pricing/getFaq"](index);

      if (faq) {
        tracking.faq({
          event: "clicked_faq",
          cta_location: faq.label,
        });
      }
    },
    trackTestimony(testimony) {
      tracking.testimony({
        event: "clicked_testimonial_nav",
        cta_location: testimony.user.title,
      });
    },
    purchaseProduct() {
      tracking.purchase({
        event: "clicked_purchase_plan_cta",
        cta_location: `${this.selectedPlan}-bottom`,
      });

      this.$router.push({
        name: "Pricing/Essentials/Review",
        params: { plan: this.selectedPlan },
      });
    },
  },
  beforeMount() {
    this.$store.commit("pricing/setProducts", window.pricing.products);
    this.$store.commit("pricing/setFeatureGroups", window.pricing.featureGroups);
    this.$store.commit("pricing/setTestimonials", window.pricing.testimonials);
    this.$store.commit("pricing/setFaqs", window.pricing.faqs.filter(faq => faq));
    this.$store.commit("pricing/setPlans", window.pricing.plans);
    this.$store.commit("pricing/setProFeatures", window.pricing.proFeatures);
    this.$store.commit("pricing/setGender", window.pricing.gender);
    this.$store.commit("pricing/setSport", window.pricing.sport);

    this.canViewInsights = window.pricing.canViewInsights;
    this.isExpiringIntegratedUser = window.pricing.isExpiringIntegratedUser;
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll, false);
    this.$nextTick(()=> {
      window.clickEssentials = () => {
        if (!['/pricing/essentials', '/pricing'].includes(this.$route.path)) {
          document.querySelector('.js-essentials-tab').click();
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth'});
        }
      };
      window.clickGuidance =  () => {
        if (this.$route.path !== '/pricing/insights') {
          var guidanceTab = document.getElementsByClassName('js-guidance-tab');
          if (guidanceTab.length > 0) {
            document.querySelector('.js-guidance-tab').click();
          } else {
            window.scrollTo({ top: 0, behavior: 'smooth'});
          }
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth'});
        }
      };
    })
  },
};
</script>

<style scoped lang="scss">
/deep/ .max-w-1200 {
  max-width: 1200px;
}

/deep/ .custom-grid-columns {
  grid-template-columns: minmax(0px, 302px) repeat(3, minmax(0, 1fr));
}

@media screen and (max-width: 1023px) {
  /deep/ .custom-grid-columns {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media screen and (max-width: 767px) {
  /deep/ .custom-grid-columns {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }
}

/deep/ .lg-header {
  font-size: 60px;
  line-height: 64px;
}

/deep/ .bg-half-white {
  background-image: linear-gradient(
    to bottom,
    white 50%,
    rgba(255, 255, 255, 0) 50%
  );
}
.upgrade-to-pro-btn {
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
  width: auto !important;
}
.cta-heading {
  max-width: 524px;
}
</style>